import {
  EMPTY_OBJECT,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../constants/common'
import { TIME_PICKER_STEP } from '../../../constants/date'
import { useFloatingSelect } from '../../../hooks'
import { stopPropagation } from '../../../utils/events'
import { ButtonArrow } from '../../Button'
import { FloatingSelectDropdown } from '../../Floating'
import { SelectContextProvider, SelectDropdownList } from '../../Select'
import { formatTimeInputValue, TimeInput } from '../TimeInput'

import { getTimePickerOptions } from './TimePicker.utils'

import * as styles from './TimePicker.module.css'

function TimePicker({
  Arrow = ButtonArrow,
  Input = TimeInput,
  List = SelectDropdownList,
  arrowProps: {
    className: arrowClassName = styles.arrow,
    ...arrowProps
  } = EMPTY_OBJECT,
  disabled,
  dropdownPlacement = FLOATING_ELEMENT_PLACEMENT.bottomStart,
  dropdownProps,
  formatLabel = formatTimeInputValue,
  listProps: {
    className: listClassName = styles.list,
    ...listProps
  } = EMPTY_OBJECT,
  maxValue,
  minValue,
  step = TIME_PICKER_STEP,
  value,
  onChange,
  ...inputProps
}) {
  const {
    activeIndex,
    context,
    elementsRef,
    floatingStyles,
    getFloatingItemProps,
    getFloatingProps,
    isOpen,
    refs,
    setOpen,
  } = useFloatingSelect({
    maxWidthByRef: true,
    placement: dropdownPlacement,
  })

  function handleInputArrowClick(event) {
    stopPropagation(event)
    setOpen(currIsOpen => !currIsOpen)
  }

  function handleInputFocus() {
    setOpen(true)
  }

  function handleInputClear() {
    setOpen(false)
  }

  function handleSelect(option) {
    onChange?.(option.value)
    setOpen(false)
  }

  const options = getTimePickerOptions({
    formatLabel,
    maxValue,
    minValue,
    step,
  })

  const selectedValue =
    options.find(option => option.label === formatLabel(value)) ?? null

  return (
    <>
      <Input
        {...inputProps}
        afterButton={
          <Arrow
            {...arrowProps}
            className={arrowClassName}
            isOpen={isOpen}
            onClick={handleInputArrowClick}
          />
        }
        containerRef={refs.setReference}
        disabled={disabled}
        maxValue={maxValue}
        minValue={minValue}
        value={value}
        onChange={onChange}
        onClear={handleInputClear}
        onFocus={handleInputFocus}
      />
      <SelectContextProvider
        activeIndex={activeIndex}
        getFloatingItemProps={getFloatingItemProps}
        isOpen={isOpen}
        value={selectedValue}
        onSelect={handleSelect}
      >
        <FloatingSelectDropdown
          {...dropdownProps}
          context={context}
          floatingStyles={floatingStyles}
          getFloatingProps={getFloatingProps}
          isOpen={isOpen}
          refs={refs}
        >
          <List
            {...listProps}
            className={listClassName}
            disabled={disabled}
            elementsRef={elementsRef}
            options={options}
          />
        </FloatingSelectDropdown>
      </SelectContextProvider>
    </>
  )
}

export default TimePicker
